import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export default function CreateTicketModal({
  open,
  onClose,
  conversation,
  onSubmit,
  zendeskEnabled = false,
  onSubmittingChange,
}) {
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const setBusy = (next) => {
    submittingRef.current = next;
    setSubmitting(next);
    onSubmittingChange?.(next);
  };

  useEffect(() => {
    if (!open || !conversation) return;
    if (submittingRef.current) return;
    setSubject(conversation.preview || "");
    setEmail(conversation.user?.email || "");
    setError("");
  }, [open, conversation]);

  useEffect(() => {
    if (!open || !submitting) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, submitting]);

  if (!open || !conversation) return null;

  const handleClose = () => {
    if (submittingRef.current) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Customer email is required to send the confirmation.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const summaryLines = (conversation.messages || [])
        .map((m) => `${m.sender}: ${m.text}`)
        .join("\n");
      await onSubmit({
        subject: subject.trim(),
        requesterEmail: trimmedEmail,
        conversationSummary: summaryLines || conversation.preview || "",
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not create ticket.";
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        aria-label="Close dialog"
        disabled={submitting}
        onClick={handleClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-ticket-title"
        className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xl shadow-slate-900/20 ring-1 ring-slate-900/[0.04]"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="create-ticket-title" className="text-lg font-semibold text-slate-900">
              Create support ticket
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              {zendeskEnabled
                ? "Opens a ticket for this conversation and mirrors it to Zendesk. Sync can take up to a minute."
                : "Opens a ticket for this conversation. The customer receives email confirmation with the ticket number."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="ui-btn-icon !h-9 !w-9 shrink-0 disabled:opacity-50"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Subject</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="ui-input-subtle w-full"
              placeholder="Short summary"
              autoComplete="off"
              disabled={submitting}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
              Confirmation email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="ui-input-subtle w-full"
              placeholder="customer@example.com"
              required
              autoComplete="email"
              disabled={submitting}
            />
          </label>
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-600 ring-1 ring-slate-100">
            Conversation <span className="font-mono font-semibold text-slate-800">{conversation.id}</span> with{" "}
            <span className="font-semibold">{conversation.user?.name}</span> will be attached to the ticket record.
          </p>
          {error && <p className="text-xs font-medium text-rose-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="ui-btn-primary !py-2 !text-sm disabled:opacity-50">
              {submitting ? "Creating ticket…" : "Create ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
